import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { useConfirm } from "@/components/useConfirm";
import { CourseAnnounceDialog } from "@/components/CourseAnnounceDialog";
import { toast } from "sonner";
import { Plus, BookOpen, Trash2, ArrowUpRight, Globe2, Lock, Pencil } from "lucide-react";

export default function CoursesList() {
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null); // {id,title,description} or null
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [announceCourse, setAnnounceCourse] = useState(null); // course that just went live
  const nav = useNavigate();
  const { confirm, ConfirmDialog } = useConfirm();

  const load = async () => {
    const { data } = await api.get("/courses");
    setCourses(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!title.trim()) return toast.error("Title is required");
    setCreating(true);
    try {
      const { data } = await api.post("/courses", { title, description });
      toast.success("Course created");
      setOpen(false); setTitle(""); setDescription("");
      nav(`/admin/courses/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const togglePublish = async (c) => {
    await api.patch(`/courses/${c.id}`, { published: !c.published });
    toast.success(c.published ? "Unpublished" : "Published");
    load();
    // Auto-prompt: draft → live means learners can see it — offer to email them.
    if (!c.published) setAnnounceCourse(c);
  };

  const openEdit = (c) => {
    setEditingCourse(c);
    setEditTitle(c.title || "");
    setEditDescription(c.description || "");
  };

  const saveEdit = async () => {
    if (!editingCourse) return;
    if (!editTitle.trim()) return toast.error("Title is required");
    setSavingEdit(true);
    try {
      await api.patch(`/courses/${editingCourse.id}`, {
        title: editTitle.trim(),
        description: editDescription,
      });
      toast.success("Course updated");
      setEditingCourse(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSavingEdit(false); }
  };

  const remove = async (c) => {
    const ok = await confirm({
      title: `Delete "${c.title}"?`,
      description: "This will permanently delete the course, all its modules, and any related chat history. This action cannot be undone.",
      confirmLabel: "Delete course",
    });
    if (!ok) return;
    try {
      await api.delete(`/courses/${c.id}`);
      toast.success("Course deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <AdminLayout>
      <div className="px-10 py-10 max-w-7xl">
        <header className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Library</div>
            <h1 className="text-4xl font-display font-bold text-slate-900 tracking-tight">Courses</h1>
            <p className="text-slate-500 mt-2">Organize learning programs by topic, team, or competency.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-course-button" className="bg-blue-900 hover:bg-blue-800 text-white">
                <Plus size={16} className="mr-1" /> New Course
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create new course</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <Label htmlFor="ctitle">Title</Label>
                  <Input id="ctitle" data-testid="course-title-input" value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="e.g., Sales Onboarding 101" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="cdesc">Description (optional)</Label>
                  <Textarea id="cdesc" data-testid="course-description-input" value={description} onChange={(e)=>setDescription(e.target.value)} rows={3} className="mt-1.5" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button data-testid="create-course-submit" disabled={creating} onClick={create} className="bg-blue-900 hover:bg-blue-800 text-white">
                  {creating ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </header>

        {courses.length === 0 ? (
          <Card className="p-16 text-center border-dashed">
            <BookOpen size={40} className="mx-auto text-slate-300 mb-3" strokeWidth={1.3} />
            <div className="text-slate-700 font-medium">No courses yet</div>
            <div className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
              Start by creating a course, then add modules from PDF, Word, audio, or video files.
            </div>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => (
              <Card key={c.id} className="p-5 border-slate-200 hover:border-blue-900/30 hover:shadow-md transition-all group" data-testid={`course-card-${c.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${c.published ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                    {c.published ? "Published · Live for learners" : "Draft · Not visible to learners"}
                  </span>
                  <div className="flex items-center gap-0.5 -mr-1.5 -mt-1">
                    <button
                      onClick={() => openEdit(c)}
                      className="p-1.5 text-slate-400 hover:text-blue-900"
                      title="Rename course"
                      data-testid={`edit-course-${c.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(c)} className="p-1.5 text-slate-400 hover:text-red-600" title="Delete" data-testid={`delete-course-${c.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <Link to={`/admin/courses/${c.id}`} className="block">
                  <h3 className="font-display font-semibold text-slate-900 mb-1 line-clamp-2 group-hover:text-blue-900 transition-colors">{c.title}</h3>
                  <p className="text-sm text-slate-500 line-clamp-2 min-h-[2.5rem]">{c.description || "No description"}</p>
                </Link>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <button
                    onClick={() => togglePublish(c)}
                    data-testid={`toggle-publish-${c.id}`}
                    className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1.5 ${
                      c.published
                        ? "border-slate-200 text-slate-600 hover:bg-slate-50"
                        : "border-blue-900 bg-blue-900 text-white hover:bg-blue-800"
                    }`}
                  >
                    {c.published ? <><Lock size={12}/> Unpublish</> : <><Globe2 size={12}/> Publish</>}
                  </button>
                  <Link to={`/admin/courses/${c.id}`} className="text-xs text-blue-900 font-medium inline-flex items-center gap-1 hover:translate-x-0.5 transition-transform">
                    Open <ArrowUpRight size={12} />
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Dialog open={!!editingCourse} onOpenChange={(o)=>{ if(!o) setEditingCourse(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit course</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="etitle">Title</Label>
              <Input
                id="etitle"
                data-testid="edit-course-title-input"
                value={editTitle}
                onChange={(e)=>setEditTitle(e.target.value)}
                className="mt-1.5"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="edesc">Description</Label>
              <Textarea
                id="edesc"
                data-testid="edit-course-description-input"
                value={editDescription}
                onChange={(e)=>setEditDescription(e.target.value)}
                rows={3}
                className="mt-1.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditingCourse(null)} data-testid="edit-course-cancel">Cancel</Button>
            <Button
              data-testid="edit-course-save"
              disabled={savingEdit}
              onClick={saveEdit}
              className="bg-blue-900 hover:bg-blue-800 text-white"
            >
              {savingEdit ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
      <CourseAnnounceDialog
        open={!!announceCourse}
        onOpenChange={(v) => { if (!v) setAnnounceCourse(null); }}
        course={announceCourse}
      />
    </AdminLayout>
  );
}